import { Component, EventEmitter, Input, Output, inject } from "@angular/core";
import { FormBuilder, FormGroup, ReactiveFormsModule, Validators } from "@angular/forms";
import { UiKitModule } from "@app/ui-kit.module";
import { ConfigurationWizardHeaderComponent } from "@components/configuration-wizard-header/configuration-wizard-header.component";
import { FilterProductComponent } from "@components/filter-product/filter-product.component";
import { RefreshProductsHintComponent } from "@components/refresh-products-hint/refresh-products-hint.component";
import { Gas, GasProduct } from "@domain/gas/gas";
import { FgdsProductType, LocalizeProductNamePipe, Product } from "@domain/product/product";
import { ProductService } from "@domain/product/product.service";
import { FgdsProject as Project } from "@domain/project/fgds-project";
import { DEFAULT_TRANSMITTER_DETECTION_RANGE } from "@lib/constants/transmitter-defaults";
import { untilDestroyed } from "@odx/angular/utils";
import { OriginalTransmitterConfiguration } from "@project/transmitter-configuration/original-transmitter-configuration";
import { TransmitterCardComponent } from "@project/transmitter-configuration/transmitter-card/transmitter-card.component";
import { TransmitterType } from "@project/transmitter-configuration/transmitter-family-selector/transmitter-type";
import { first, map } from "rxjs";

@Component({
  selector: "app-transmitter-selector",
  templateUrl: "./transmitter-selector.component.html",
  styleUrls: ["./transmitter-selector.component.scss"],
  imports: [
    UiKitModule,
    ReactiveFormsModule,
    ConfigurationWizardHeaderComponent,
    FilterProductComponent,
    RefreshProductsHintComponent,
    TransmitterCardComponent,
    LocalizeProductNamePipe,
  ],
  providers: [LocalizeProductNamePipe],
  standalone: true,
})
export class TransmitterSelectorComponent {
  private productService = inject(ProductService);
  private formBuilder = inject(FormBuilder);

  protected readonly defaultDetectionRangeInfoText: string = $localize`:@@transmitterConfiguration.defaultDetectionRange.hint:Standardwert für neue Transmitter. Kann pro Transmitter manuell überschrieben werden.`;

  @Input({ required: true }) set selectedGasData(gas: Gas | undefined) {
    if (this.originalTransmitterConfiguration?.gas) {
      this.clearTransmitterSelection();
    }
    this.clearProducts();
    if (gas) {
      this.selectedGas = gas;
      if (this.selectedTransmitterFamily) {
        this.collectProducts();
      }
    }
  }

  @Input({ required: true }) showCosts!: boolean;
  @Input() project!: Project;
  @Input() set selectedFamily(family: TransmitterType | undefined) {
    this.selectedTransmitterFamily = family;
    this.clearTransmitterSelection();
    this.clearProducts();
    if (this.selectedGas && this.selectedTransmitterFamily) {
      this.collectProducts();
    }
  }
  @Input() set defaultDetectionRange(value: number) {
    this.defaultDetectionRangeValue = value;
  }

  @Input() set originalConfiguration(transmitterConfiguration: OriginalTransmitterConfiguration | undefined) {
    if (!transmitterConfiguration) {
      return;
    }
    this.originalTransmitterConfiguration = transmitterConfiguration;
    if (transmitterConfiguration.transmitter && transmitterConfiguration.transmitter.isAvailable) {
      this.select(transmitterConfiguration.transmitter);
    }
  }

  @Output() transmitterSelect$ = new EventEmitter<Product>();
  @Output() defaultDetectionRangeChange = new EventEmitter<number>();

  protected selectedGas?: Gas;
  protected selectedTransmitter?: Product;
  protected filteredSelectableProducts: Product[] = [];
  protected selectedTransmitterForm: FormGroup;
  protected defaultDetectionRangeValue = DEFAULT_TRANSMITTER_DETECTION_RANGE;

  private originalTransmitterConfiguration?: OriginalTransmitterConfiguration;
  private selectedTransmitterFamily?: TransmitterType;
  private selectableProducts: Product[] = [];
  private selectableGasProducts: GasProduct[] = [];
  private readonly takeUntilDestroyed = untilDestroyed();

  constructor() {
    const formBuilder = this.formBuilder;

    this.selectedTransmitterForm = formBuilder.group({
      selectedTransmitter: [this.originalConfiguration || null, Validators.required],
    });

    this.selectedTransmitterForm.valueChanges.pipe(this.takeUntilDestroyed()).subscribe(() => {
      this.selectedTransmitter = this.selectedTransmitterForm.value.selectedTransmitter;
      return this.transmitterSelect$.emit(this.selectedTransmitter);
    });
  }

  protected search(value: string) {
    this.filteredSelectableProducts = this.selectableProducts.filter((product) =>
      this.productService.searchFilter(product, value),
    );
  }

  protected select(transmitter: Product) {
    this.selectedTransmitterForm.patchValue({
      selectedTransmitter: transmitter,
    });
  }

  protected onDefaultDetectionRangeInput(value: string) {
    const parsed = Number(value);
    if (!Number.isFinite(parsed) || parsed <= 0) {
      return;
    }
    this.defaultDetectionRangeValue = parsed;
    this.defaultDetectionRangeChange.emit(parsed);
  }

  private collectProducts() {
    if (!this.selectedGas || !this.selectedTransmitterFamily) {
      return;
    }

    this.selectableGasProducts = this.selectedGas!.products;
    const ids = this.selectableGasProducts.filter((gasProduct) => gasProduct.productId).map((gasProduct) => gasProduct.productId);

    if (ids.length) {
      this.productService
        .getProductsByIds(ids, FgdsProductType.TRANSMITTER)
        .pipe(
          map((products: Product[]) =>
            Product.sort(products).filter((product) => {
              if (!product.isAvailable) return false;
              if (!this.selectedTransmitterFamily) return true;

              const gasProduct = this.selectableGasProducts.find((entry) => entry.productId === product.id);
              // `rawName` is only a legacy fallback for gas data cached before parentFamilies existed.
              const familyNames = product.parentFamilies.length ? product.parentFamilies : [gasProduct?.rawName];
              return familyNames.some(
                (familyName) => familyName?.trim().toLowerCase() === this.selectedTransmitterFamily!.name.trim().toLowerCase(),
              );
            }),
          ),
          first(),
        )
        .subscribe((products: Product[]) => this.setProducts(products));
    }
  }

  private clearProducts() {
    this.setProducts([]);
  }

  private setProducts(products: Product[]) {
    this.selectableProducts = products;
    this.filteredSelectableProducts = products;

    const originalTransmitter = this.originalTransmitterConfiguration?.transmitter;
    if (originalTransmitter?.isAvailable && products.some((product) => product.id === originalTransmitter.id)) {
      this.select(originalTransmitter);
    }
  }

  private clearTransmitterSelection() {
    this.selectedTransmitterForm.patchValue({ selectedTransmitter: undefined });
  }
}
